// ============================================================
// APP TESTING PAGE
// ============================================================

import React, { useState, useEffect } from 'react';
import { Terminal, Play, RefreshCw, CheckCircle, XCircle, Clock } from 'lucide-react';
import { Table, Badge, Button, StatCard, Tabs } from '../components/ui';
import { getTestLogs } from '../services/mockData';
import { formatDateTime } from '../utils/helpers';
import { toast } from 'react-toastify';

const AppTesting = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  useEffect(() => { setTimeout(() => { setLogs(getTestLogs()); setLoading(false); }, 600); }, []);

  const runAllTests = () => {
    setRunning(true);
    toast.info('Running all test suites...');
    setTimeout(() => {
      setLogs(p => p.map(l => ({ ...l, status: Math.random() > 0.15 ? 'passed' : 'failed', runAt: new Date().toISOString() })));
      setRunning(false);
      toast.success('Test run complete!');
    }, 2500);
  };

  const passed = logs.filter(l => l.status === 'passed').length;
  const failed = logs.filter(l => l.status === 'failed').length;
  const skipped = logs.filter(l => l.status === 'skipped').length;

  const tabOptions = ['All', 'passed', 'failed', 'skipped'];
  const [activeTab, setActiveTab] = useState('All');
  const filtered = activeTab === 'All' ? logs : logs.filter(l => l.status === activeTab);

  const columns = [
    { key: 'testName', label: 'Test Name', render: v => <b>{v}</b> },
    { key: 'module', label: 'Module' },
    { key: 'type', label: 'Type', render: v => <Badge status="info" text={v} /> },
    {
      key: 'status', label: 'Status',
      render: v => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: v === 'passed' ? '#10B981' : v === 'failed' ? '#EF4444' : '#94A3B8' }}>
          {v === 'passed' ? <CheckCircle size={14} /> : v === 'failed' ? <XCircle size={14} /> : <Clock size={14} />}
          {v}
        </span>
      )
    },
    { key: 'duration', label: 'Duration', render: v => `${v}ms` },
    { key: 'runAt', label: 'Last Run', render: v => formatDateTime(v) },
    {
      key: 'error', label: 'Error',
      render: v => v ? <span style={{ color: '#EF4444', fontSize: 12 }}>{v.slice(0, 50)}…</span> : <span style={{ color: '#94A3B8' }}>—</span>
    },
  ];

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">App Testing & QA</h1>
          <p className="admin-page-subtitle">Monitor automated test results and QA status</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="secondary" icon={<RefreshCw size={14} />} onClick={() => { setLoading(true); setTimeout(() => { setLogs(getTestLogs()); setLoading(false); }, 600); }}>Refresh</Button>
          <Button variant="primary" icon={<Play size={14} />} onClick={runAllTests} disabled={running}>
            {running ? 'Running Tests…' : 'Run All Tests'}
          </Button>
        </div>
      </div>

      <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 24 }}>
        <StatCard label="Total Tests" value={logs.length} icon={<Terminal size={18} />} color="#3B82F6" />
        <StatCard label="Passed" value={passed} icon={<CheckCircle size={18} />} color="#10B981" />
        <StatCard label="Failed" value={failed} icon={<XCircle size={18} />} color="#EF4444" />
        <StatCard label="Pass Rate" value={logs.length ? `${((passed / logs.length) * 100).toFixed(1)}%` : '—'} icon={<Terminal size={18} />} color="#8B5CF6" />
      </div>

      {/* Progress Bar */}
      <div className="admin-card" style={{ marginBottom: 24 }}>
        <div className="admin-card-body" style={{ padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontWeight: 600 }}>Test Suite Health</span>
            <span style={{ color: '#10B981', fontWeight: 700 }}>{logs.length ? ((passed / logs.length) * 100).toFixed(1) : 0}% Passing</span>
          </div>
          <div style={{ height: 12, borderRadius: 6, background: 'var(--admin-border)', overflow: 'hidden', display: 'flex' }}>
            <div style={{ height: '100%', width: `${logs.length ? (passed / logs.length) * 100 : 0}%`, background: '#10B981', transition: 'width 0.8s ease' }} />
            <div style={{ height: '100%', width: `${logs.length ? (failed / logs.length) * 100 : 0}%`, background: '#EF4444' }} />
            <div style={{ height: '100%', width: `${logs.length ? (skipped / logs.length) * 100 : 0}%`, background: '#F59E0B' }} />
          </div>
          <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 12, color: '#94A3B8' }}>
            <span>🟢 Passed: {passed}</span><span>🔴 Failed: {failed}</span><span>🟡 Skipped: {skipped}</span>
          </div>
        </div>
      </div>

      <div className="admin-table-wrapper">
        <div className="admin-table-toolbar">
          <div style={{ display: 'flex', gap: 4 }}>
            {tabOptions.map(t => (
              <button key={t} onClick={() => setActiveTab(t)} style={{
                padding: '4px 12px', borderRadius: 6, border: '1px solid var(--admin-border)', cursor: 'pointer',
                background: activeTab === t ? '#E85D04' : 'transparent', color: activeTab === t ? '#fff' : 'inherit',
                fontSize: 13, fontWeight: 600,
              }}>{t === 'All' ? 'All' : t.charAt(0).toUpperCase() + t.slice(1)}</button>
            ))}
          </div>
          <span style={{ fontSize: 14, fontWeight: 700 }}>{filtered.length} tests</span>
        </div>
        <Table columns={columns} data={filtered} loading={loading} />
      </div>
    </div>
  );
};

export default AppTesting;
